import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { FiArrowRight } from 'react-icons/fi';
import './DevAvatar.css';

/* ---------- colours of the character ---------- */
const SKIN = '#e8b088';
const SKIN_SHADE = '#c98f66';
const HAIR = '#14141c';
const INK = '#1a1a22';
const MOUTH = '#6e2430';

/* ---------- what the avatar says per page (+ quick action buttons) ---------- */
const ROUTE_MOOD = {
  '/': ['happy', "Hi, I'm Amit. Full stack developer 👋"],
  '/skills': ['cool', 'Hover the cubes to see my skills 😎'],
  '/projects': ['excited', 'Real projects I built. Pick one 🚀'],
  '/timeline': ['thinking', 'Hover a year to see my journey…'],
  '/certificates': ['proud', 'Courses and certificates 🏆'],
  '/contact': ['wink', 'Drop me a message here 📞']
};

const ROUTE_ACTIONS = {
  '/': [['Projects', '/projects'], ['Contact', '/contact']],
  '/skills': [['See projects', '/projects']],
  '/projects': [['Journey', '/timeline'], ['Hire me', '/contact']],
  '/timeline': [['Certificates', '/certificates']],
  '/certificates': [["Let's talk", '/contact']],
  '/contact': [['Back home', '/']]
};

const BUTTON_POOL = [
  ['happy', 'Nice click! ✨'],
  ['cool', 'Smooth 😎'],
  ['laugh', 'Haha 😄']
];

const COFFEE_POOL = [
  ['sip', 'Coffee break ☕'],
  ['sip', 'Fuel for more code ☕'],
  ['sip', 'Ahh, fresh brew ☕']
];

const ANYWHERE_POOL = [
  ['surprised', 'Whoa! 😮'],
  ['laugh', 'Haha 😄'],
  ['cool', 'Smooth 😎'],
  ['love', 'Aww, thanks 💙'],
  ['thinking', 'Hmm, interesting…'],
  ['wink', 'Psst… 😉'],
  ['happy', 'Hello! 👋'],
  ['proud', 'Nailed it 💪']
];

/* ---------- face recipe for every mood ---------- */
const FACE = {
  neutral:   { eyes: 'open',   brows: 'flat',  mouth: 'smile' },
  happy:     { eyes: 'arc',    brows: 'up',    mouth: 'big',      blush: true },
  laugh:     { eyes: 'arc',    brows: 'up',    mouth: 'laugh',    blush: true },
  wink:      { eyes: 'wink',   brows: 'up',    mouth: 'smirkBig', blush: true },
  surprised: { eyes: 'wide',   brows: 'high',  mouth: 'o' },
  thinking:  { eyes: 'look',   brows: 'think', mouth: 'smirk' },
  cool:      { eyes: 'shades', brows: 'flat',  mouth: 'smirk' },
  love:      { eyes: 'heart',  brows: 'up',    mouth: 'big',      blush: true },
  sleepy:    { eyes: 'sleep',  brows: 'relax', mouth: 'yawn' },
  focused:   { eyes: 'narrow', brows: 'focus', mouth: 'flat' },
  excited:   { eyes: 'star',   brows: 'high',  mouth: 'laugh',    blush: true },
  proud:     { eyes: 'open',   brows: 'up',    mouth: 'big',      blush: true },
  sip:       { eyes: 'arc',    brows: 'relax', mouth: 'smile' }
};

/* every brow path has the same shape so framer-motion can morph between them */
const BROWS = {
  flat:  ['M72 71 Q82 67 92 70', 'M108 70 Q118 67 128 71'],
  up:    ['M72 66 Q82 61 92 65', 'M108 65 Q118 61 128 66'],
  high:  ['M72 63 Q82 58 92 62', 'M108 62 Q118 58 128 63'],
  think: ['M72 64 Q82 58 92 64', 'M108 71 Q118 70 128 73'],
  focus: ['M72 68 Q82 70 92 74', 'M108 74 Q118 70 128 68'],
  relax: ['M72 72 Q82 72 92 74', 'M108 74 Q118 72 128 72']
};

/* head movement per mood */
const HEAD = {
  neutral:   { rotate: 0,  y: 0, scale: 1 },
  happy:     { rotate: [0, -4, 3, 0], y: [0, -3, 0], scale: 1 },
  laugh:     { rotate: [0, -5, 5, -5, 5, 0], y: [0, -2, 0, -2, 0], scale: 1 },
  wink:      { rotate: [0, 6, 4], y: 0, scale: 1 },
  surprised: { rotate: 0, y: [0, -5, -2], scale: [1, 1.06, 1.03] },
  thinking:  { rotate: -6, y: 0, scale: 1 },
  cool:      { rotate: 0, y: [0, -2, 0], scale: 1 },
  love:      { rotate: [0, 3, -3, 0], y: 0, scale: [1, 1.05, 1] },
  sleepy:    { rotate: 7, y: 4, scale: 1 },
  focused:   { rotate: 0, y: 3, scale: 1 },
  excited:   { rotate: [0, -6, 6, -4, 0], y: [0, -6, 0, -4, 0], scale: [1, 1.05, 1] },
  proud:     { rotate: -4, y: -3, scale: 1 },
  sip:       { rotate: -6, y: 2, scale: 1 }
};

/* ---------- arms: only TWO arms exist. Each has shoulder angle + elbow angle (right arm values, left arm is mirrored) ---------- */
const ANG = {
  rest:  [0, 119],     
  wave:  [-100, -60],
  up:    [-80, -60],
  cheer: [-160, 0],
  think: [40, 110],   
  point: [-90, -90]    
};

/* what the arms do per mood (r = right arm, l = left arm) */
const POSES = {
  neutral:   { r: 'rest',  l: 'rest' },
  happy:     { r: 'rest',  l: 'wave' },
  laugh:     { r: 'rest',  l: 'rest' },
  wink:      { r: 'point', l: 'rest' },
  surprised: { r: 'up',    l: 'up' },
  thinking:  { r: 'think', l: 'rest' },
  cool:      { r: 'rest',  l: 'rest' },
  love:      { r: 'rest',  l: 'wave' },
  sleepy:    { r: 'rest',  l: 'rest' },
  focused:   { r: 'rest',  l: 'rest' },
  excited:   { r: 'cheer', l: 'cheer' },
  proud:     { r: 'rest',  l: 'rest' },
  sip:       { r: 'rest',  l: 'rest' }
};

/* ---------- small SVG pieces (face coordinates) ---------- */
const OpenEye = ({ cx, rx = 9, ry = 10, pr = 5, look, track = true }) => (
  <g className="eye-open">
    <ellipse cx={cx} cy="85" rx={rx} ry={ry} fill="#f7f4f1" />
    <ellipse cx={cx} cy={85 + ry * 0.55} rx={rx * 0.9} ry={ry * 0.45} fill="#d9cfc7" opacity="0.35" />
    <g
      className={track && !look ? 'pupil track' : 'pupil'}
      style={look ? { transform: `translate(${look[0]}px, ${look[1]}px)` } : undefined}
    >
      <circle cx={cx} cy="85" r={pr + 0.9} fill="url(#da-iris)" />
      <circle cx={cx} cy="85" r={pr * 0.55} fill={INK} />
      <circle cx={cx + 1.8} cy="83" r={pr * 0.3} fill="#fff" />
      <circle cx={cx - 1.6} cy="87" r={pr * 0.16} fill="#fff" opacity="0.6" />
    </g>
    <path d={`M${cx - rx} 85 Q${cx} ${85 - ry - 2} ${cx + rx} 85`} fill="none" stroke={INK} strokeWidth="2.4" opacity="0.85" />
    <path d={`M${cx - rx + 1} 86 Q${cx} ${85 + ry + 1} ${cx + rx - 1} 86`} fill="none" stroke={SKIN_SHADE} strokeWidth="1" opacity="0.6" />
  </g>
);

const Arc = ({ cx, up = true }) => (
  <path
    d={up ? `M${cx - 9} 87 Q${cx} 76 ${cx + 9} 87` : `M${cx - 9} 85 Q${cx} 91 ${cx + 9} 85`}
    fill="none" stroke={INK} strokeWidth="3.6" strokeLinecap="round"
  />
);

const Heart = ({ cx }) => (
  <g transform={`translate(${cx} 86)`}>
    <path className="heart-eye" d="M0 5 C-11 -3 -9 -12 0 -7 C9 -12 11 -3 0 5Z" fill="#ff4d6d" />
  </g>
);

const Star = ({ cx }) => (
  <g transform={`translate(${cx} 85)`}>
    <path className="star-eye" d="M0 -10 L2.8 -2.8 L10 0 L2.8 2.8 L0 10 L-2.8 2.8 L-10 0 L-2.8 -2.8Z" fill="#ffd54a" />
  </g>
);

/* normal glasses (always on, except when the dark shades are shown) */
const Glasses = () => (
  <g className="glasses">
    <rect x="65" y="73" width="33" height="24" rx="10" fill="rgba(160,210,255,0.12)" stroke="#1d2330" strokeWidth="3" />
    <rect x="102" y="73" width="33" height="24" rx="10" fill="rgba(160,210,255,0.12)" stroke="#1d2330" strokeWidth="3" />
    <path d="M71 76 L83 76 L74 95 L68 95 Z" fill="url(#da-glass)" />
    <path d="M108 76 L120 76 L111 95 L105 95 Z" fill="url(#da-glass)" />
    <path d="M98 82 Q100 79 102 82" fill="none" stroke="#1d2330" strokeWidth="3" />
    <path d="M65 80 L55 78 M135 80 L145 78" stroke="#1d2330" strokeWidth="3" strokeLinecap="round" />
    <path className="accent-stroke" d="M70 79 L76 79" strokeWidth="1.6" strokeLinecap="round" opacity="0.7" />
  </g>
);

const Eyes = ({ type }) => {
  switch (type) {
    case 'wide':  return <><OpenEye cx={82} rx={10.5} ry={12} pr={3.8} /><OpenEye cx={118} rx={10.5} ry={12} pr={3.8} /></>;
    case 'look':  return <><OpenEye cx={82} look={[3, -3]} /><OpenEye cx={118} look={[3, -3]} /></>;
    case 'narrow':return <><OpenEye cx={82} ry={5.4} pr={3.6} look={[0, 1.6]} /><OpenEye cx={118} ry={5.4} pr={3.6} look={[0, 1.6]} /></>;
    case 'arc':   return <><Arc cx={82} /><Arc cx={118} /></>;
    case 'wink':  return <><OpenEye cx={82} /><Arc cx={118} /></>;
    case 'sleep': return <><Arc cx={82} up={false} /><Arc cx={118} up={false} /></>;
    case 'heart': return <><Heart cx={82} /><Heart cx={118} /></>;
    case 'star':  return <><Star cx={82} /><Star cx={118} /></>;
    case 'shades':
      return (
        <g className="shades">
          <rect x="66" y="74" width="31" height="21" rx="8" fill="#0c0f14" />
          <rect x="103" y="74" width="31" height="21" rx="8" fill="#0c0f14" />
          <path d="M97 82 H103 M66 80 L56 78 M134 80 L144 78" stroke="#0c0f14" strokeWidth="3" strokeLinecap="round" />
          <path className="accent-stroke shade-glint" d="M71 80 L78 80 M108 80 L115 80" strokeWidth="2" strokeLinecap="round" />
        </g>
      );
    default:      return <><OpenEye cx={82} /><OpenEye cx={118} /></>;
  }
};

const Mouth = ({ type, talking }) => {
  const teeth = (y, h) => (
    <path d={`M87 ${y} Q100 ${y + 5} 113 ${y} L113 ${y + h} Q100 ${y + h + 5} 87 ${y + h} Z`} fill="#fff" />
  );

  /* talking: closed mouths become an opening / closing mouth */
  if (talking && ['smile', 'smirk', 'smirkBig', 'flat'].includes(type)) {
    return (
      <g className="mouth-talking">
        <path d="M88 108 Q100 106 112 108 Q112 124 100 124 Q88 124 88 108 Z" fill={MOUTH} />
        <path d="M91 108.5 Q100 107.5 109 108.5 L109 111.5 Q100 110.5 91 111.5 Z" fill="#fff" />
        <ellipse cx="100" cy="120" rx="6" ry="3" fill="#e0707a" />
      </g>
    );
  }

  const open = (node) => (talking ? <g className="mouth-talking">{node}</g> : node);

  switch (type) {
    case 'big':
      return open(
        <g>
          <path d="M84 108 Q100 134 116 108 Z" fill={MOUTH} />
          {teeth(109, 2.5)}
          <ellipse cx="100" cy="116.5" rx="7" ry="3.5" fill="#e0707a" />
        </g>
      );
    case 'laugh':
      return (
        <g className="mouth-laugh">
          <path d="M81 106 Q100 142 119 106 Z" fill={MOUTH} />
          {teeth(107, 3.5)}
          <ellipse cx="100" cy="118" rx="8" ry="4" fill="#e0707a" />
        </g>
      );
    case 'o':      return open(<ellipse cx="100" cy="117" rx="6" ry="8" fill={MOUTH} />);
    case 'yawn':   return <ellipse className="mouth-yawn" cx="100" cy="116" rx="5" ry="6.5" fill={MOUTH} />;
    case 'smirk':  return <path className="m-line" d="M88 114 Q100 119 113 109" />;
    case 'smirkBig': return <path className="m-line" d="M86 110 Q101 126 116 106" />;
    case 'flat':   return <path className="m-line" d="M91 115 Q100 113 109 115" />;
    default:       return <path className="m-line" d="M86 111 Q100 123 114 111" />;
  }
};

/* mood specific floating effects (face coordinates) */
const Fx = ({ mood }) => {
  switch (mood) {
    case 'love':
      return (
        <g className="fx">
          {[[42, 78, 0], [158, 70, 0.6], [150, 108, 1.2]].map(([x, y, d]) => (
            <g key={x} transform={`translate(${x} ${y}) scale(.8)`}>
              <path className="fx-float" style={{ animationDelay: `${d}s` }}
                d="M0 5 C-11 -3 -9 -12 0 -7 C9 -12 11 -3 0 5Z" fill="#ff4d6d" />
            </g>
          ))}
        </g>
      );
    case 'excited':
    case 'proud':
      return (
        <g className="fx">
          {[[44, 56, 0], [158, 52, 0.5], [150, 104, 1]].map(([x, y, d]) => (
            <g key={x} transform={`translate(${x} ${y})`}>
              <path className="twinkle accent-fill" style={{ animationDelay: `${d}s` }}
                d="M0 -8 L2.2 -2.2 L8 0 L2.2 2.2 L0 8 L-2.2 2.2 L-8 0 L-2.2 -2.2Z" />
            </g>
          ))}
        </g>
      );
    case 'surprised':
      return <g className="fx"><text className="fx-bang" x="152" y="52">!</text></g>;
    case 'thinking':
      return (
        <g className="fx">
          <circle className="fx-dot" cx="150" cy="56" r="2.6" style={{ animationDelay: '0s' }} />
          <circle className="fx-dot" cx="160" cy="44" r="3.6" style={{ animationDelay: '0.25s' }} />
          <circle className="fx-dot" cx="172" cy="28" r="5" style={{ animationDelay: '0.5s' }} />
        </g>
      );
    case 'sleepy':
      return (
        <g className="fx">
          {[[142, 54, 0, 12], [154, 40, 0.8, 15], [168, 24, 1.6, 18]].map(([x, y, d, s]) => (
            <text key={x} className="fx-z" x={x} y={y} fontSize={s} style={{ animationDelay: `${d}s` }}>z</text>
          ))}
        </g>
      );
    case 'laugh':
      return (
        <g className="fx">
          <path className="fx-tear" d="M70 92 q-3 6 0 9 q3 -3 0 -9" fill="#6ec6ff" />
          <path className="fx-tear" style={{ animationDelay: '0.4s' }} d="M130 92 q-3 6 0 9 q3 -3 0 -9" fill="#6ec6ff" />
        </g>
      );
    case 'wink':
    case 'cool':
      return (
        <g className="fx">
          <g transform="translate(138 74)">
            <path className="twinkle accent-fill" d="M0 -7 L2 -2 L7 0 L2 2 L0 7 L-2 2 L-7 0 L-2 -2Z" />
          </g>
        </g>
      );
    default:
      return null;
  }
};

/* ---------- one arm: shoulder -> elbow -> wrist -> hand. Only two of these exist in the whole drawing ---------- */
const Arm = ({ side, pose }) => {
  const right = side === 'r';
  const k = right ? 1 : -1;
  const [sh, el] = ANG[pose] || ANG.rest;

  return (
    <g transform={`translate(${right ? 210 : 70} 176)`}>
      <g
        className={`arm arm-${side}`}
        data-pose={pose}
        style={{ '--s': `${sh * k}deg`, '--e': `${el * k}deg` }}
      >
        {/* upper arm */}
        <line className="sleeve-edge" x1="0" y1="0" x2="0" y2="38" strokeWidth="25" strokeLinecap="round" />
        <line className="sleeve" x1="0" y1="0" x2="0" y2="38" strokeWidth="22" strokeLinecap="round" />

        {/* forearm (pivot = elbow) */}
        <g transform="translate(0 38)">
          <g className="fore">
            <line className="sleeve-edge" x1="0" y1="0" x2="0" y2="34" strokeWidth="23" strokeLinecap="round" />
            <line className="sleeve" x1="0" y1="0" x2="0" y2="34" strokeWidth="20" strokeLinecap="round" />
            <line className="cuff" x1="0" y1="31" x2="0" y2="36" strokeWidth="21" />

            {/* hand (pivot = wrist) */}
            <g transform="translate(0 36)">
              <g className="hand">
                <ellipse cx={-9 * k} cy="5" rx="3.8" ry="6.5" fill={SKIN}
                  transform={`rotate(${-22 * k} ${-9 * k} 5)`} />
                <ellipse cx="0" cy="9" rx="10" ry="11" fill="url(#da-skin)" />
                <g className="fingers">
                  <rect x="-9" y="16" width="4.6" height="13" rx="2.3" fill={SKIN} />
                  <rect x="-4" y="17" width="4.6" height="15" rx="2.3" fill={SKIN} />
                  <rect x="1" y="17" width="4.6" height="15" rx="2.3" fill={SKIN} />
                  <rect x="6" y="16" width="4.2" height="12" rx="2.1" fill={SKIN} />
                </g>
                {right && (
                  <g className="finger"><rect x="-3" y="16" width="6" height="17" rx="3" fill={SKIN} /></g>
                )}
              </g>
            </g>
          </g>
        </g>
      </g>
    </g>
  );
};

/* ---------- the character ---------- */
const Character = ({ mood, svgRef, rPose, lPose, typing, talking }) => {
  const f = FACE[mood] || FACE.neutral;
  const [bl, br] = BROWS[f.brows];
  const talk = talking && mood !== 'sleepy';

  return (
    <svg
      ref={svgRef}
      className="dev-svg"
      viewBox="0 0 280 238"
      data-rpose={rPose}
      data-typing={typing ? '1' : '0'}
      data-talking={talk ? '1' : '0'}
      data-sip={mood === 'sip' ? '1' : '0'}
      aria-hidden="true"
      focusable="false"
    >
      <defs>
        <linearGradient id="da-skin" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#f3c39b" /><stop offset="1" stopColor="#dc9f72" />
        </linearGradient>
        <radialGradient id="da-face" cx="0.4" cy="0.36" r="0.78">
          <stop offset="0" stopColor="#f8cfa9" />
          <stop offset="0.55" stopColor="#e8b088" />
          <stop offset="1" stopColor="#bf8359" />
        </radialGradient>
        <radialGradient id="da-ear" cx="0.4" cy="0.4" r="0.8">
          <stop offset="0" stopColor="#efbb93" /><stop offset="1" stopColor="#c48760" />
        </radialGradient>
        <linearGradient id="da-neck" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#a96b44" /><stop offset="1" stopColor="#d89c70" />
        </linearGradient>
        <linearGradient id="da-hair" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#4a4a66" /><stop offset="0.5" stopColor="#23232f" /><stop offset="1" stopColor="#0f0f16" />
        </linearGradient>
        <linearGradient id="da-beard" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#262632" /><stop offset="1" stopColor="#0d0d13" />
        </linearGradient>
        <radialGradient id="da-iris" cx="0.5" cy="0.4" r="0.7">
          <stop offset="0" stopColor="#9a6a48" /><stop offset="1" stopColor="#4a2c1c" />
        </radialGradient>
        <linearGradient id="da-glass" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="#ffffff" stopOpacity="0.38" />
          <stop offset="1" stopColor="#ffffff" stopOpacity="0" />
        </linearGradient>
        <linearGradient id="da-lid" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#2c3443" /><stop offset="1" stopColor="#141922" />
        </linearGradient>
        <linearGradient id="da-hoodie" x1="0" y1="0" x2="0" y2="1">
          <stop className="stop-h1" offset="0" stopColor="#2e3a4e" />
          <stop className="stop-h2" offset="1" stopColor="#121824" />
        </linearGradient>
        <linearGradient id="da-hoodie-dark" x1="0" y1="0" x2="0" y2="1">
          <stop className="stop-hd1" offset="0" stopColor="#1e2736" />
          <stop className="stop-hd2" offset="1" stopColor="#0b0f16" />
        </linearGradient>
      </defs>

      {/* drifting code glyphs */}
      <g className="glyphs">
        <text className="glyph g1" x="20" y="64">{'{ }'}</text>
        <text className="glyph g2" x="206" y="38">{'</>'}</text>
        <text className="glyph g3" x="214" y="92">{'=>'}</text>
      </g>

      {/* torso: blazer + white shirt + tie */}
      <g className="torso">
        <path d="M72 172 C67 130 95 112 140 112 C185 112 213 130 208 172 Z" fill="url(#da-hoodie-dark)" />
        <rect x="123" y="130" width="34" height="36" rx="13" fill="url(#da-neck)" />
        <path d="M37 238 C37 182 77 160 140 160 C203 160 243 182 243 238 Z" fill="url(#da-hoodie)" />
        <path d="M58 196 C68 172 100 162 140 162" fill="none" stroke="rgba(255,255,255,0.10)" strokeWidth="3" strokeLinecap="round" />
        <path d="M97 170 C89 190 85 212 87 238 M183 170 C191 190 195 212 193 238" fill="none" stroke="rgba(0,0,0,0.22)" strokeWidth="2" />
        {/* white shirt */}
        <path d="M113 156 L140 200 L167 156 L158 150 C150 160 130 160 122 150 Z" fill="#f4f6f8" />
        <path d="M122 150 C130 160 150 160 158 150 L158 153 C150 164 130 164 122 153 Z" fill="rgba(0,0,0,0.12)" />
        {/* tie */}
        <path className="accent-fill" d="M137.5 168 L142.5 168 L144.5 198 L140 206 L135.5 198 Z" />
        {/* blazer lapels */}
        <path d="M113 156 L129 194 L114 208 L94 170 Z" fill="url(#da-hoodie-dark)" stroke="rgba(255,255,255,0.14)" strokeWidth="1" />
        <path d="M167 156 L151 194 L166 208 L186 170 Z" fill="url(#da-hoodie-dark)" stroke="rgba(255,255,255,0.14)" strokeWidth="1" />
        {/* shadow of the chin on the neck */}
        <ellipse cx="140" cy="150" rx="26" ry="8" fill="rgba(0,0,0,0.28)" />
      </g>

      {/* laptop (arms are drawn after it, so the hands rest on top) */}
      <g className="laptop">
        <path d="M62 190 Q62 182 70 182 L210 182 Q218 182 218 190 L224 240 L56 240 Z" fill="url(#da-lid)" stroke="#2c3647" strokeWidth="1.5" />
        <path d="M72 183.5 L208 183.5" stroke="rgba(255,255,255,0.2)" strokeWidth="1.2" />
        <text className="laptop-logo" x="140" y="214" textAnchor="middle">{'</>'}</text>
      </g>

      {/* coffee mug */}
      <g className="mug">
        <g className="steam">
          <path className="steam-1" d="M243 206 q-4 -7 0 -13 q4 -6 0 -12" />
          <path className="steam-2" d="M252 206 q-4 -7 0 -13 q4 -6 0 -12" />
        </g>
        <rect x="236" y="208" width="26" height="28" rx="6" fill="#f2efe9" />
        <path d="M262 214 q11 0 11 9 q0 9 -11 9" fill="none" stroke="#f2efe9" strokeWidth="4" />
        <rect x="238" y="210" width="22" height="7" rx="3" fill="#6b3f22" />
        <rect x="239" y="212" width="4" height="22" rx="2" fill="rgba(0,0,0,0.08)" />
      </g>

      {/* head (follows the mouse a little, like a real 3D head) */}
      <g className="head-look">
        <motion.g
          className="head"
          initial={false}
          animate={HEAD[mood] || HEAD.neutral}
          transition={{ duration: 0.7, ease: 'easeOut' }}
          style={{ originX: 0.5, originY: 0.95 }}
        >
          <g className="head-inner">
            <g transform="translate(40 18)">
              {/* ears */}
              <circle cx="56" cy="92" r="8.5" fill="url(#da-ear)" />
              <circle cx="144" cy="92" r="8.5" fill="url(#da-ear)" />
              <circle cx="56" cy="92" r="4" fill={SKIN_SHADE} opacity="0.5" />
              <circle cx="144" cy="92" r="4" fill={SKIN_SHADE} opacity="0.5" />

              {/* head shape with round shading */}
              <path d="M54 86 C54 52 74 40 100 40 C126 40 146 52 146 86 C146 118 128 142 100 144 C72 142 54 118 54 86 Z" fill="url(#da-face)" />
              <ellipse cx="100" cy="68" rx="24" ry="9" fill="#fff" opacity="0.09" />
              <path d="M126 52 C142 62 148 80 146 96 C144 120 128 140 102 144 C128 134 138 112 138 90 C138 74 134 62 126 52 Z" fill="#9b6340" opacity="0.18" />

              {/* full beard + skin patch around the mouth */}
              <path d="M55 96 C54 130 76 148 100 148 C124 148 146 130 145 96 C141 108 132 112 122 112 C114 106 86 106 78 112 C68 112 59 108 55 96 Z" fill="url(#da-beard)" opacity="0.96" />
              <path d="M66 112 C70 132 84 142 100 143" fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth="2" strokeLinecap="round" />
              <path d="M86 108 Q100 104 114 108 Q118 124 100 126 Q82 124 86 108 Z" fill="url(#da-skin)" />

              {/* hair: side-swept quiff */}
              <path d="M53 86 C43 40 74 20 104 22 C136 22 158 44 147 86 C143 66 132 56 116 52 C100 58 78 58 62 68 C57 74 55 80 53 86 Z" fill="url(#da-hair)" />
              <path d="M70 52 C66 24 96 6 126 14 C146 20 156 34 150 52 C142 40 128 32 112 32 C94 32 80 40 70 52 Z" fill="url(#da-hair)" />
              <path d="M74 34 Q104 12 138 28" fill="none" stroke="rgba(255,255,255,0.22)" strokeWidth="2.4" strokeLinecap="round" />
              <path d="M84 40 Q108 22 134 34 M92 46 Q112 32 138 42" fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth="1.4" strokeLinecap="round" />

              {/* face (re-created per mood so it "pops" into the new look) */}
              <g className="face-shift">
                <motion.g key={mood} initial={{ opacity: 0.2 }} animate={{ opacity: 1 }} transition={{ duration: 0.25 }}>
                  {/* eye sockets + cheek light give depth */}
                  <ellipse cx="82" cy="87" rx="14" ry="11" fill={SKIN_SHADE} opacity="0.16" />
                  <ellipse cx="118" cy="87" rx="14" ry="11" fill={SKIN_SHADE} opacity="0.16" />
                  <ellipse cx="72" cy="101" rx="9" ry="6" fill="#fff" opacity="0.08" />
                  <ellipse cx="128" cy="101" rx="9" ry="6" fill="#fff" opacity="0.05" />
                  {f.blush && (
                    <>
                      <ellipse cx="70" cy="103" rx="7" ry="4.5" fill="#ff7a8a" opacity="0.35" />
                      <ellipse cx="130" cy="103" rx="7" ry="4.5" fill="#ff7a8a" opacity="0.35" />
                    </>
                  )}
                  <Eyes type={f.eyes} />
                  {f.eyes !== 'shades' && <Glasses />}

                  {/* nose with light and shadow */}
                  <ellipse cx="101" cy="91" rx="2.2" ry="7" fill="#fff" opacity="0.2" />
                  <path d="M97 84 Q95 98 94 103" fill="none" stroke={SKIN_SHADE} strokeWidth="1.6" strokeLinecap="round" opacity="0.5" />
                  <ellipse cx="100" cy="102" rx="7" ry="5" fill={SKIN_SHADE} opacity="0.28" />
                  <path d="M93 104 Q100 109 107 104" fill="none" stroke={SKIN_SHADE} strokeWidth="2.4" strokeLinecap="round" />

                  <Mouth type={f.mouth} talking={talk} />
                  {/* moustache */}
                  <path d="M82 107 C86 98 96 100 100 105 C104 100 114 98 118 107 C112 111 106 108 100 109 C94 108 88 111 82 107 Z" fill="url(#da-beard)" />
                </motion.g>

                {/* eyebrows morph smoothly from one mood to the next */}
                <motion.path className="brow" initial={false} animate={{ d: bl }} transition={{ duration: 0.35, ease: 'easeOut' }} />
                <motion.path className="brow" initial={false} animate={{ d: br }} transition={{ duration: 0.35, ease: 'easeOut' }} />
              </g>
            </g>
          </g>
        </motion.g>
      </g>

      {/* the two arms (drawn after the head so a hand can touch the chin) */}
      <Arm side="l" pose={lPose} />
      <Arm side="r" pose={rPose} />

      {/* holographic button the finger taps */}
      <g className="holo" transform="translate(258 100)">
        <g className="holo-inner">
          <rect className="holo-rect" x="-13" y="-9" width="26" height="18" rx="6" />
          <path className="holo-cursor" d="M-3 -4 L-3 5 L0 2.5 L3 6.5 L5 5.5 L2 1.5 L6 1 Z" />
        </g>
        <circle className="holo-ring" r="9" />
      </g>

      <g transform="translate(40 18)">
        <Fx key={`fx-${mood}`} mood={mood} />
      </g>
    </svg>
  );
};

/* choose a random reaction, never the same text twice in a row */
let lastPickText = '';
const pick = (pool) => {
  const options = pool.filter(([, t]) => t !== lastPickText);
  const choice = options[Math.floor(Math.random() * options.length)];
  lastPickText = choice[1];
  return choice;
};

/* ---------- the logic ---------- */
const DevAvatar = ({ page = 'home' }) => {
  const { pathname } = useLocation();
  const navigate = useNavigate();

  const [mood, setMood] = useState('neutral');
  const [bubble, setBubble] = useState(null);
  const [typed, setTyped] = useState('');
  const [typedDone, setTypedDone] = useState(false);
  const [talking, setTalking] = useState(false);
  const [pressing, setPressing] = useState(false);
  const [typingIdle, setTypingIdle] = useState(false);
  const [anchor, setAnchor] = useState(null);

  const svgRef = useRef(null);
  const wrapRef = useRef(null);
  const timers = useRef({});
  const lastActivity = useRef(Date.now());
  const sleeping = useRef(false);
  const seq = useRef(0);
  const firstRoute = useRef(true);
  const pathRef = useRef(pathname);
  const moodRef = useRef(mood);
  pathRef.current = pathname;
  moodRef.current = mood;

  const hideBubbleIn = useCallback((ms) => {
    clearTimeout(timers.current.bubble);
    timers.current.bubble = setTimeout(() => setBubble(null), ms);
  }, []);

  /* the finger taps its own holographic button */
  const press = useCallback(() => {
    setPressing(true);
    clearTimeout(timers.current.press);
    timers.current.press = setTimeout(() => setPressing(false), 1800);
  }, []);

  const react = useCallback((m, text, ms = 3400, actions = null) => {
    sleeping.current = false;
    clearTimeout(timers.current.mood);
    setMood(m);
    if (text) {
      setBubble({ id: ++seq.current, text, actions });
      hideBubbleIn(actions ? 7500 : Math.max(2400, ms - 200));
    }
    timers.current.mood = setTimeout(() => setMood('neutral'), ms);
  }, [hideBubbleIn]);

  /* typewriter: the avatar "talks" while the text types out */
  useEffect(() => {
    if (!bubble) { setTyped(''); setTypedDone(false); setTalking(false); return undefined; }
    const chars = Array.from(bubble.text);
    const reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduce) { setTyped(bubble.text); setTypedDone(true); return undefined; }

    let i = 0;
    let endTimer;
    setTyped('');
    setTypedDone(false);
    setTalking(true);
    const iv = setInterval(() => {
      i += 1;
      setTyped(chars.slice(0, i).join(''));
      if (i >= chars.length) {
        clearInterval(iv);
        setTypedDone(true);
        endTimer = setTimeout(() => setTalking(false), 220);
      }
    }, 32);
    return () => { clearInterval(iv); clearTimeout(endTimer); setTalking(false); };
  }, [bubble]);

  /* keep the bubble next to the sidebar (it pops out to the right of the avatar) */
  useEffect(() => {
    if (!bubble) return undefined;
    const place = () => {
      const el = wrapRef.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      if (!r.width) { setAnchor(null); return; }
      const side = el.closest('.sidebar');
      const right = side ? side.getBoundingClientRect().right : r.right;
      setAnchor({
        left: right + 14,
        bottom: Math.max(12, window.innerHeight - (r.top + r.height * 0.62))
      });
    };
    place();
    window.addEventListener('resize', place);
    return () => window.removeEventListener('resize', place);
  }, [bubble]);

  /* greeting once */
  useEffect(() => {
    const t = setTimeout(
      () => react('happy', 'Hi! Click anywhere, I react 🙂', 4200, [['Explore projects', '/projects']]),
      1200
    );
    return () => clearTimeout(t);
  }, [react]);

  /* react to page changes */
  useEffect(() => {
    if (firstRoute.current) { firstRoute.current = false; return; }
    const seg = pathname.split('/')[1];
    const key = seg ? `/${seg}` : '/';
    const [m, text] = ROUTE_MOOD[key] || ROUTE_MOOD['/'];
    react(m, text, 3600, ROUTE_ACTIONS[key] || null);
  }, [pathname, react]);

  /* now and then he types on his laptop */
  useEffect(() => {
    const t = timers.current;
    const iv = setInterval(() => {
      if (moodRef.current === 'neutral' && !sleeping.current) {
        setTypingIdle(true);
        clearTimeout(t.idleType);
        t.idleType = setTimeout(() => setTypingIdle(false), 2600);
      }
    }, 9000);
    return () => { clearInterval(iv); clearTimeout(t.idleType); };
  }, []);

  /* now and then he takes a coffee break */
  useEffect(() => {
    const iv = setInterval(() => {
      if (moodRef.current === 'neutral' && !sleeping.current) {
        const [m, text] = pick(COFFEE_POOL);
        react(m, text, 3200);
      }
    }, 40000);
    return () => clearInterval(iv);
  }, [react]);

  /* page-wide listeners: eyes + head follow the mouse, clicks and typing change the mood */
  useEffect(() => {
    let raf = 0;

    const wake = () => {
      lastActivity.current = Date.now();
      if (sleeping.current) react('surprised', "Oh, you're back! 👀", 2600);
    };

    const onMove = (e) => {
      wake();
      if (raf) return;
      raf = requestAnimationFrame(() => {
        raf = 0;
        const el = svgRef.current;
        if (!el) return;
        const r = el.getBoundingClientRect();
        const dx = e.clientX - (r.left + r.width / 2);
        const dy = e.clientY - (r.top + r.height * 0.45);
        const d = Math.hypot(dx, dy) || 1;
        const k = Math.min(1, d / 280);
        el.style.setProperty('--ex', `${(dx / d) * 3.4 * k}px`);
        el.style.setProperty('--ey', `${(dy / d) * 3 * k}px`);

        /* 3D feel: head turns, face shifts, whole card tilts */
        const nx = Math.max(-1, Math.min(1, dx / 420));
        const ny = Math.max(-1, Math.min(1, dy / 420));
        el.style.setProperty('--hx', `${(nx * 2.6).toFixed(2)}px`);
        el.style.setProperty('--hy', `${(ny * 1.8).toFixed(2)}px`);
        el.style.setProperty('--hr', `${(nx * 3).toFixed(2)}deg`);
        const w = wrapRef.current;
        if (w) {
          w.style.setProperty('--ry', `${(nx * 9).toFixed(2)}deg`);
          w.style.setProperty('--rx', `${(-ny * 7).toFixed(2)}deg`);
        }
      });
    };

    const onKey = (e) => {
      wake();
      if (e.target instanceof Element && e.target.closest('input, textarea')) {
        react('focused', null, 2200);
      }
    };

    const onClick = (e) => {
      lastActivity.current = Date.now();
      sleeping.current = false;
      const t = e.target;
      if (!(t instanceof Element)) return;

      if (t.closest('.dev-bubble-portal')) return; 

      press(); 

      const custom = t.closest('[data-avatar-mood]');
      if (custom) return react(custom.dataset.avatarMood, custom.dataset.avatarText || null);

      if (t.closest('.dev-stage')) return react('laugh', 'Hehe, that tickles! 😆');

      const nav = t.closest('.nav-item, .mobile-nav-item');
      if (nav) {
        if (nav.getAttribute('href') === pathRef.current) return react('wink', "You're already here 😄");
        return; 
      }

      const link = t.closest('a[href]');
      if (link) {
        const href = link.getAttribute('href') || '';
        if (href.startsWith('mailto:')) return react('love', "Can't wait to hear from you 💌");
        if (/^https?:/.test(href)) return react('wink', 'Good pick 😉');
      }

      if (t.closest('input, textarea, select, label')) return react('focused', "I'm all ears ⌨️", 3000);
      if (t.closest('button[type="submit"], .submit-btn')) return react('excited', 'Sending… fingers crossed 🤞');
      if (t.closest('button, [role="button"], a')) { const [m, text] = pick(BUTTON_POOL); return react(m, text); }

      const [m, text] = pick(ANYWHERE_POOL);
      react(m, text);
    };

    const sleepTimer = setInterval(() => {
      if (!sleeping.current && Date.now() - lastActivity.current > 30000) {
        sleeping.current = true;
        clearTimeout(timers.current.mood);
        setMood('sleepy');
        setBubble({ id: ++seq.current, text: 'zzz… 😴', actions: null });
        hideBubbleIn(2600);
      }
    }, 4000);

    document.addEventListener('click', onClick, true);
    window.addEventListener('mousemove', onMove, { passive: true });
    window.addEventListener('keydown', onKey, true);
    window.addEventListener('scroll', wake, { passive: true, capture: true });
    window.addEventListener('touchstart', wake, { passive: true });

    const savedTimers = timers.current;
    return () => {
      cancelAnimationFrame(raf);
      clearInterval(sleepTimer);
      document.removeEventListener('click', onClick, true);
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('keydown', onKey, true);
      window.removeEventListener('scroll', wake, true);
      window.removeEventListener('touchstart', wake);
      Object.values(savedTimers).forEach(clearTimeout);
    };
  }, [react, press, hideBubbleIn]);

  const base = POSES[mood] || POSES.neutral;
  const rPose = pressing ? 'point' : base.r;
  const lPose = base.l;
  const typing = typingIdle || mood === 'focused';

  const goTo = (to) => {
    press();
    setBubble(null);
    navigate(to);
  };

  return (
    <>
      <div className="sb-dev" ref={wrapRef}>
        <div className="dev-stage" aria-hidden="true">
          <div className="dev-tilt">
            <Character
              mood={mood}
              svgRef={svgRef}
              rPose={rPose}
              lPose={lPose}
              typing={typing}
              talking={talking}
            />
          </div>
        </div>
      </div>

      {createPortal(
        <div
          className="dev-bubble-portal"
          data-sb-page={page}
          style={anchor ? { left: anchor.left, bottom: anchor.bottom } : undefined}
        >
          <AnimatePresence>
            {bubble && anchor && (
              <motion.div
                key={bubble.id}
                className={`dev-bubble${bubble.actions ? ' has-actions' : ''}`}
                initial={{ opacity: 0, x: -12, scale: 0.94 }}
                animate={{ opacity: 1, x: 0, scale: 1 }}
                exit={{ opacity: 0, x: -6, scale: 0.96 }}
                transition={{ type: 'spring', stiffness: 420, damping: 30 }}
                onMouseEnter={() => clearTimeout(timers.current.bubble)}
                onMouseLeave={() => hideBubbleIn(2200)}
              >
                <div className="dev-bubble-text">
                  <span className="ghost">{bubble.text}</span>
                  <span className="typed">
                    {typed}
                    {!typedDone && <i className="caret"></i>}
                  </span>
                </div>

                {bubble.actions && typedDone && (
                  <motion.div
                    className="dev-actions"
                    initial={{ opacity: 0, y: 6 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3 }}
                  >
                    {bubble.actions.map(([label, to]) => (
                      <button key={to} type="button" className="dev-act" onClick={() => goTo(to)}>
                        {label}
                        <FiArrowRight />
                      </button>
                    ))}
                  </motion.div>
                )}
              </motion.div>
            )}
          </AnimatePresence>
        </div>,
        document.body
      )}
    </>
  );
};

export default DevAvatar;